import React, { useEffect, useState } from 'react';
import { X, Send, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { mensajeErrorApi } from '../apiErrors';
import SeleccionarClienteInput from './SeleccionarClienteInput';

const TRANSPORTISTAS = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'amazon', label: 'Amazon Logistics' },
  { value: 'dhl', label: 'DHL' },
  { value: 'otro', label: 'Otro' },
];

const PAISES = [
  { value: 'usa', label: 'Estados Unidos (Miami)' },
  { value: 'china', label: 'China (Shenzhen)' },
];

const CATEGORIAS = [
  { value: 'b', label: 'Categoría B (4x4)' },
  { value: 'c', label: 'Categoría C' },
  { value: 'comercial', label: 'Importación comercial' },
  { value: 'documentos', label: 'Documentos' },
  { value: 'especial', label: 'Requiere permiso / tratamiento especial' },
];

function datosDesdeVacio(prellenado) {
  return {
    tracking: '', guia: '', transportista: 'ups', tienda: '', orden_o_factura: '',
    pais_origen: 'usa', bodega: '', categoria_aduanera: 'b', estado_actual: '',
    descripcion: '', valor_declarado: '', moneda: 'USD', num_piezas: 1,
    peso_real: '', largo: '', ancho: '', alto: '', tipo_empaque: '',
    instrucciones_especiales: '', desea_consolidar: 'no',
    ...(prellenado?.datos || {}),
  };
}

function datosDesdePaquete(p) {
  return {
    tracking: p.tracking || '', guia: p.guia || '', transportista: p.transportista || 'ups',
    tienda: p.tienda || '', orden_o_factura: p.orden_o_factura || '',
    pais_origen: p.pais_origen || 'usa', bodega: p.bodega || '',
    categoria_aduanera: p.categoria_aduanera || 'b', estado_actual: p.estado_actual || '',
    descripcion: p.descripcion || '', valor_declarado: p.valor_declarado || '',
    moneda: p.moneda || 'USD', num_piezas: p.num_piezas || 1,
    peso_real: p.peso_real || '', largo: p.largo || '', ancho: p.ancho || '', alto: p.alto || '',
    tipo_empaque: p.tipo_empaque || '',
    instrucciones_especiales: p.instrucciones_especiales || '',
    desea_consolidar: p.desea_consolidar || 'no',
  };
}

/**
 * Crear/editar un paquete desde el panel de staff (admin/asistente). A
 * diferencia de PrealertaModal (el formulario reducido que usa el cliente),
 * acá se pueden tocar todos los campos: guía, peso, categoría, estado
 * inicial, y a qué cliente pertenece (con buscador, no un <select> de 300+).
 *
 * `prellenado` (Fase 4 de Compra Asistida / Puerta a Puerta):
 * { cliente, datos, origen: 'compra-asistida' | 'puerta-a-puerta', solicitudId, etiqueta }
 * — si viene, el paquete nace vinculado a esa solicitud (POST va al endpoint
 * dedicado de ese origen en vez de /paquetes/, así el backend fuerza el
 * cliente correcto y guarda el vínculo) y arranca con cliente/descripción
 * ya resueltos. `etiqueta` es el nombre legible para el título del modal.
 */
export default function PaqueteFormModal({ paquete, prellenado, onClose, onGuardado }) {
  const editando = Boolean(paquete);
  const [bodegas, setBodegas] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cliente, setCliente] = useState(
    editando ? (paquete.cliente_detalle || null) : (prellenado?.cliente || null)
  );
  const [datos, setDatos] = useState(editando ? datosDesdePaquete(paquete) : datosDesdeVacio(prellenado));
  const [factura, setFactura] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    api.get('/bodegas/').then((res) => setBodegas(res.data.results || res.data)).catch(() => {});
    api.get('/estados/').then((res) => {
      const lista = res.data.results || res.data;
      setEstados(lista);
      if (!editando) {
        const inicial = lista.find((e) => e.codigo === 'prealertado');
        if (inicial) setDatos((d) => ({ ...d, estado_actual: inicial.id }));
      }
    }).catch(() => {});
    if (!editando) {
      // Preselección de bodega según país por defecto, una vez cargadas.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editando || !bodegas.length || datos.bodega) return;
    const porDefecto = bodegas.find((b) => b.pais === 'Estados Unidos') || bodegas[0];
    if (porDefecto) setDatos((d) => ({ ...d, bodega: porDefecto.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodegas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cliente) return toast.error('Selecciona el cliente dueño del paquete.');
    if (!datos.tracking.trim()) return toast.error('El número de tracking es obligatorio.');
    if (!datos.bodega) return toast.error('Selecciona la bodega de origen.');
    if (!editando && !datos.estado_actual) return toast.error('Selecciona el estado inicial.');

    setGuardando(true);
    try {
      const form = new FormData();
      form.append('cliente', cliente.id);
      Object.entries(datos).forEach(([k, v]) => {
        if (v !== '' && v !== null) form.append(k, v);
      });
      if (factura) form.append('factura_adjunta', factura);

      const urlCreacion = prellenado
        ? `/${prellenado.origen}/${prellenado.solicitudId}/crear-paquete/`
        : '/paquetes/';
      const res = editando
        ? await api.patch(`/paquetes/${paquete.id}/`, form, { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post(urlCreacion, form, { headers: { 'Content-Type': 'multipart/form-data' } });

      toast.success(editando ? 'Paquete actualizado.' : 'Paquete creado.');
      onGuardado(res.data);
    } catch (err) {
      toast.error(mensajeErrorApi(err, 'No se pudo guardar el paquete.'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>
            {editando
              ? `Editar paquete — ${paquete.tracking || `#${paquete.id}`}`
              : prellenado ? `Nuevo paquete — ${prellenado.etiqueta} #${prellenado.solicitudId}` : 'Nuevo paquete'}
          </h3>
          <button className="btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="form-stack">
          <SeleccionarClienteInput seleccionado={cliente} onSelect={setCliente} />

          <div className="form-row">
            <div>
              <label>Tracking *</label>
              <input value={datos.tracking} onChange={(e) => setDatos({ ...datos, tracking: e.target.value })} required />
            </div>
            <div>
              <label>Guía interna</label>
              <input value={datos.guia} onChange={(e) => setDatos({ ...datos, guia: e.target.value })} placeholder="ECA##MTC####" />
            </div>
            <div>
              <label>Transportista</label>
              <select value={datos.transportista} onChange={(e) => setDatos({ ...datos, transportista: e.target.value })}>
                {TRANSPORTISTAS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>País de origen</label>
              <select value={datos.pais_origen} onChange={(e) => setDatos({ ...datos, pais_origen: e.target.value })}>
                {PAISES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label>Bodega</label>
              <select value={datos.bodega} onChange={(e) => setDatos({ ...datos, bodega: e.target.value })}>
                <option value="">—</option>
                {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
              </select>
            </div>
            <div>
              <label>Categoría aduanera</label>
              <select value={datos.categoria_aduanera} onChange={(e) => setDatos({ ...datos, categoria_aduanera: e.target.value })}>
                {CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {!editando && (
            <div>
              <label>Estado inicial</label>
              <select value={datos.estado_actual} onChange={(e) => setDatos({ ...datos, estado_actual: e.target.value })}>
                <option value="">—</option>
                {estados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
              </select>
            </div>
          )}

          <div className="form-row">
            <div>
              <label>Tienda</label>
              <input value={datos.tienda} onChange={(e) => setDatos({ ...datos, tienda: e.target.value })} />
            </div>
            <div>
              <label>Orden / factura</label>
              <input value={datos.orden_o_factura} onChange={(e) => setDatos({ ...datos, orden_o_factura: e.target.value })} />
            </div>
          </div>

          <div>
            <label>Descripción del contenido</label>
            <textarea rows={2} value={datos.descripcion} onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })} />
          </div>

          <div className="form-row">
            <div>
              <label>Valor declarado (USD)</label>
              <input type="number" step="0.01" min="0" value={datos.valor_declarado}
                onChange={(e) => setDatos({ ...datos, valor_declarado: e.target.value })} />
            </div>
            <div>
              <label>Piezas</label>
              <input type="number" min="1" value={datos.num_piezas}
                onChange={(e) => setDatos({ ...datos, num_piezas: e.target.value })} />
            </div>
            <div>
              <label>Peso real (lb)</label>
              <input type="number" step="0.01" min="0" value={datos.peso_real}
                onChange={(e) => setDatos({ ...datos, peso_real: e.target.value })} />
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>Largo (cm)</label>
              <input type="number" step="0.01" min="0" value={datos.largo} onChange={(e) => setDatos({ ...datos, largo: e.target.value })} />
            </div>
            <div>
              <label>Ancho (cm)</label>
              <input type="number" step="0.01" min="0" value={datos.ancho} onChange={(e) => setDatos({ ...datos, ancho: e.target.value })} />
            </div>
            <div>
              <label>Alto (cm)</label>
              <input type="number" step="0.01" min="0" value={datos.alto} onChange={(e) => setDatos({ ...datos, alto: e.target.value })} />
            </div>
          </div>

          <div className="form-row">
            <div>
              <label>¿Desea consolidar?</label>
              <select value={datos.desea_consolidar} onChange={(e) => setDatos({ ...datos, desea_consolidar: e.target.value })}>
                <option value="no">No - Enviar en cuanto llegue</option>
                <option value="si">Sí - Esperar otros paquetes (Costo extra)</option>
              </select>
            </div>
          </div>

          <div>
            <label>Instrucciones especiales</label>
            <textarea rows={2} value={datos.instrucciones_especiales}
              onChange={(e) => setDatos({ ...datos, instrucciones_especiales: e.target.value })}
              placeholder="Paquete frágil, esperar otros paquetes, reempacar, etc." />
          </div>

          <div>
            <label>Factura {editando ? '(reemplazar)' : '(opcional)'}</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFactura(e.target.files[0])} />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardando}>
              {editando ? <Save size={16} /> : <Send size={16} />}
              {guardando ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear paquete'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
